import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  UserPlus, Mail, Phone, RotateCcw, X, Check, Loader2, AlertTriangle,
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/contexts/AuthContext'
import {
  useContributorInvitationsQuery,
  useSendContributorInviteEmailMutation,
  useSendContributorInviteSmsMutation,
  useResendContributorInviteMutation,
  useCancelContributorInviteMutation,
  type ContributorInvitation,
} from '@/data/contributorInvitations'

interface Props {
  dropId: string
  dropName: string
  organizationId: string
  contributorToken: string | null
}

type InviteMethod = 'email' | 'sms'

const statusStyles: Record<ContributorInvitation['status'], string> = {
  pending: 'bg-amber-500/10 text-amber-600',
  accepted: 'bg-emerald-500/10 text-emerald-600',
  expired: 'bg-muted text-muted-foreground',
  cancelled: 'bg-muted text-muted-foreground line-through',
}

export function ContributorInvitationsSection({
  dropId,
  dropName,
  organizationId,
  contributorToken,
}: Props) {
  const { toast } = useToast()
  const { user } = useAuth()
  const inviterName = user?.name ?? undefined

  const { data: invitations = [], isLoading } = useContributorInvitationsQuery(dropId)
  const sendEmailMutation = useSendContributorInviteEmailMutation()
  const sendSmsMutation = useSendContributorInviteSmsMutation()
  const resendMutation = useResendContributorInviteMutation(dropId)
  const cancelMutation = useCancelContributorInviteMutation(dropId)

  const [showModal, setShowModal] = useState(false)
  const [method, setMethod] = useState<InviteMethod>('email')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [smsConsent, setSmsConsent] = useState(false)
  const [error, setError] = useState('')

  const isSending = sendEmailMutation.isPending || sendSmsMutation.isPending

  const closeModal = () => {
    setShowModal(false)
    setError('')
    setName('')
    setEmail('')
    setPhone('')
    setSmsConsent(false)
    setMethod('email')
  }

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!contributorToken) {
      setError('This drop has no contributor token; cannot send invitations.')
      return
    }

    if (method === 'email') {
      if (!email.includes('@')) {
        setError('Please enter a valid email address.')
        return
      }
      try {
        await sendEmailMutation.mutateAsync({
          dropId,
          organizationId,
          recipientEmail: email,
          recipientName: name || undefined,
          inviterName,
          dropName,
          contributorToken,
        })
        toast({ title: 'Invitation sent', description: `Emailed ${email}.` })
        closeModal()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send invitation.')
      }
      return
    }

    // SMS
    if (!/^\+[1-9]\d{6,14}$/.test(phone)) {
      setError('Phone must be in E.164 format (e.g. +15551234567).')
      return
    }
    if (!smsConsent) {
      setError('You must confirm the recipient agreed to receive a text before sending.')
      return
    }
    try {
      await sendSmsMutation.mutateAsync({
        dropId,
        organizationId,
        recipientPhone: phone,
        recipientName: name || undefined,
        inviterName,
        dropName,
        contributorToken,
        consentConfirmed: smsConsent,
      })
      toast({ title: 'Invitation sent', description: `Texted ${phone}.` })
      closeModal()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation.')
    }
  }

  const handleResend = async (invitationId: string, label: string) => {
    try {
      await resendMutation.mutateAsync(invitationId)
      toast({ title: 'Invitation resent', description: `Sent again to ${label}.` })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to resend',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    }
  }

  const handleCancel = async (invitationId: string) => {
    try {
      await cancelMutation.mutateAsync(invitationId)
      toast({ title: 'Invitation cancelled' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to cancel',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    }
  }

  return (
    <div className="bg-card rounded-xl border p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold">Contributor Invitations</h3>
          <p className="text-sm text-muted-foreground">
            Invite specific people via email or SMS to upload to this drop.
          </p>
        </div>
        <Button onClick={() => setShowModal(true)} disabled={!contributorToken}>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite Contributor
        </Button>
      </div>

      {isLoading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : invitations.length === 0 ? (
        <p className="py-6 text-sm text-muted-foreground text-center">
          No invitations sent yet.
        </p>
      ) : (
        <div className="divide-y">
          {invitations.map((inv) => {
            const recipient =
              inv.recipient_name || inv.recipient_email || inv.recipient_phone || 'Unknown'
            const contact = inv.recipient_email || inv.recipient_phone || ''
            const isPending = inv.status === 'pending'
            const isResending = resendMutation.isPending && resendMutation.variables === inv.id
            const isCancelling = cancelMutation.isPending && cancelMutation.variables === inv.id
            return (
              <div key={inv.id} className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-muted text-muted-foreground flex items-center justify-center shrink-0">
                    {inv.method === 'sms' ? <Phone className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{recipient}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {contact}
                      {inv.resend_count > 0 && (
                        <span className="ml-2">
                          · Resent {inv.resend_count} time{inv.resend_count !== 1 ? 's' : ''}
                        </span>
                      )}
                      <span className="ml-2">
                        · {new Date(inv.invited_at).toLocaleDateString()}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[inv.status]}`}>
                    {inv.status}
                  </span>
                  {isPending && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResend(inv.id, recipient)}
                        disabled={isResending}
                        title="Resend invitation"
                      >
                        {isResending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleCancel(inv.id)}
                        disabled={isCancelling}
                        title="Cancel invitation"
                      >
                        {isCancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                      </Button>
                    </>
                  )}
                  {inv.status === 'accepted' && (
                    <Check className="h-4 w-4 text-emerald-600" />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {showModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={closeModal} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <form
              onSubmit={handleSend}
              className="bg-card text-card-foreground rounded-lg border shadow-xl max-w-md w-full p-6 space-y-4 relative"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Invite Contributor</h3>
                <button
                  type="button"
                  onClick={closeModal}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {error && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-3 py-2 rounded-lg text-sm flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMethod('email')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm border ${
                    method === 'email'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border'
                  }`}
                >
                  <Mail className="inline h-4 w-4 mr-2" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setMethod('sms')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm border ${
                    method === 'sms'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border'
                  }`}
                >
                  <Phone className="inline h-4 w-4 mr-2" />
                  SMS
                </button>
              </div>

              <div>
                <label htmlFor="invite-name" className="block text-sm font-medium mb-1">
                  Name <span className="text-muted-foreground">(optional)</span>
                </label>
                <input
                  id="invite-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Friend's name"
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>

              {method === 'email' ? (
                <div>
                  <label htmlFor="invite-email" className="block text-sm font-medium mb-1">
                    Email
                  </label>
                  <input
                    id="invite-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="friend@example.com"
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    required
                  />
                </div>
              ) : (
                <div>
                  <label htmlFor="invite-phone" className="block text-sm font-medium mb-1">
                    Phone (E.164)
                  </label>
                  <input
                    id="invite-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+15551234567"
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    International format, e.g. +1 for US.
                  </p>

                  <label className="flex items-start gap-2 mt-3 text-sm">
                    <input
                      type="checkbox"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      className="mt-0.5"
                      required
                    />
                    <span>
                      I confirm this person agreed to receive a text message from me about this
                      photo drop. They can reply STOP to opt out.
                    </span>
                  </label>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={closeModal} disabled={isSending}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSending || !contributorToken}>
                  {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {isSending ? 'Sending…' : 'Send Invitation'}
                </Button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  )
}
